'use client';

import React, { useState } from 'react';

interface OnboardingModalProps {
  onComplete: (name: string, gender: string) => void;
}

export default function OnboardingModal({ onComplete }: OnboardingModalProps) {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!gender) { setError('Please select your gender'); return; }
    onComplete(name.trim(), gender);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-sm mx-4 shadow-2xl">
        
        {/* Logo */}
        <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 border-2 border-primary/40">
          <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.876V15.124a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
        </div>

        <h2 className="text-foreground text-xl font-bold text-center mb-1">Welcome to ChatConnect</h2>
        <p className="text-muted-foreground text-sm text-center mb-6">Tell us about yourself to get started</p>

        {/* Name Input */}
        <div className="mb-4">
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">YOUR NAME</label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="Enter your name..."
            maxLength={30}
            className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
          />
        </div>

        {/* Gender Select */}
        <div className="mb-6">
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">YOU ARE</label>
          <div className="flex gap-2">
            {[
              { value: 'male', label: '♂ Male' },
              { value: 'female', label: '♀ Female' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setGender(opt.value); setError(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 border ${
                  gender === opt.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-background border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && <p className="text-destructive text-xs mb-4 text-center">{error}</p>}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          className="w-full bg-primary text-white py-3 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors"
        >
          Continue →
        </button>

        <p className="text-muted-foreground text-xs text-center mt-4">
          This info is only used for matching. Not stored permanently.
        </p>
      </div>
    </div>
  );
}
